/**
 * Client content pack store (P4.9).
 *
 * Holds the presentation-only pack the client has loaded and reconciles it
 * against the hash the server announces in `helloAck.contentPackHash`: on a
 * mismatch it refetches `GET /api/content-pack/<hash>.json` over the same HTTPS
 * origin (vite proxies `/api` in dev, Caddy in prod). The detect-and-refetch
 * decision is the shared, unit-tested `syncContentPack`; this class only adds
 * the real fetch and the in-memory cache. The pack is presentation data
 * (names, icons, telegraph visuals, tooltips) — it never gates joining.
 */

import {
  syncContentPack,
  type ClientContentPack,
  type ClientPackDef,
  type ContentCategory,
  type ContentPackState,
} from '@great-cloud/shared/client';

import { serverHttpBase } from '../net/platform.js';

type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

export class ContentPackStore {
  private state: ContentPackState = { hash: '', pack: null };

  constructor(
    private readonly fetchImpl: FetchLike = (input, init) => fetch(input, init),
    private readonly httpBase = serverHttpBase(),
  ) {}

  /** Hash of the loaded pack (`''` when none) — sent to the server in `hello`. */
  get hash(): string {
    return this.state.hash;
  }

  get pack(): ClientContentPack | null {
    return this.state.pack;
  }

  /**
   * Reconcile against the server-announced hash, refetching only on a mismatch.
   * Returns true when a refetch happened. Errors propagate to the caller (the
   * pack is non-critical, so the caller logs and continues).
   */
  async sync(announcedHash: string): Promise<boolean> {
    const result = await syncContentPack(this.state, announcedHash, (hash) => this.fetchPack(hash));
    this.state = result.state;
    return result.refetched;
  }

  /** Presentation lookup for client rendering (names/icons/tooltips). */
  get(category: ContentCategory, id: string): ClientPackDef | undefined {
    return this.state.pack?.categories[category]?.[id];
  }

  private async fetchPack(hash: string): Promise<ClientContentPack> {
    const base = this.httpBase.replace(/\/+$/, '');
    const res = await this.fetchImpl(`${base}/api/content-pack/${hash}.json`, {
      headers: { accept: 'application/json' },
    });
    if (!res.ok) throw new Error(`content pack fetch failed (${res.status})`);
    return (await res.json()) as ClientContentPack;
  }
}
